import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-final-form';

import { AnimatedButton } from '../../AnimatedButton/AnimatedButton';
import { ButtonColorway } from '../../ButtonV3/types';
import Portal from '../../util/Portal';
import { LinkV2 } from '../../LinkV2';

import { ONBOARDING_PORTAL_CONTENT, useOnboardingContext } from '../context';

import {
  AnimatedButtonWrapper,
  Container,
  Hed,
  Dek,
  StepContent,
  StyledSubmitButton,
  AllFieldsRequired,
  LoginCta,
  StyledTermsPrivacy,
  MobileFixedBtnContainer,
} from './OnboardStep.styles';

export type OnboardStepProps = {
  hed?: string | ReactNode;
  dek?: string | ReactNode;
  allFieldsRequired?: boolean;
  isValid?: boolean;
  showLoginCta?: boolean;
  nextButtonConfettiColors?: string[];
  nextButtonShowColorAnimation?: boolean;
  nextButtonShowConfettiAnimation?: boolean;
  nextButtonSuccessColorway?: ButtonColorway;
  nextButtonSuccessText?: string;
  nextButtonText?: string;
  nextButtonClick?: () => void;
  className?: string;
  portalContent?: ReactNode;
  disableButton?: boolean;
  showTermsPrivacy?: boolean;
};

const OnboardStep: React.FC<OnboardStepProps> = ({
  hed,
  dek,
  isValid,
  allFieldsRequired,
  showLoginCta,
  nextButtonConfettiColors,
  nextButtonShowColorAnimation = false,
  nextButtonShowConfettiAnimation = false,
  nextButtonSuccessColorway,
  nextButtonSuccessText,
  nextButtonText,
  portalContent,
  className,
  children,
  disableButton = false,
  nextButtonClick,
  showTermsPrivacy = true,
}) => {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const { submitting, hasValidationErrors } = useFormState();
  const { onboardingState, toggleIsLogin } = useOnboardingContext();
  const { portalContainerRef, step, sectionStepsLength, currentUserContext } = onboardingState;
  const isLastStep = step === sectionStepsLength - 1;
  const disabled = useMemo(
    () =>
      hasValidationErrors ||
      (typeof isValid !== 'undefined' && !isValid) ||
      submitting ||
      disableButton,
    [disableButton, hasValidationErrors, isValid, submitting]
  );
  const sharedButtonProps = useMemo(
    () => ({
      disabled,
      fullWidth: true,
      onClick: nextButtonClick,
      size: 'large' as const,
      type: 'submit' as const,
    }),
    [disabled, nextButtonClick]
  );
  const sharedButtonText = useMemo(
    () => nextButtonText || (isLastStep ? 'Submit' : 'Next'),
    [isLastStep, nextButtonText]
  );

  useEffect(() => {
    const container = document.getElementById(ONBOARDING_PORTAL_CONTENT);
    if (container && !portalContainer) {
      setPortalContainer(container);
    }
  }, [portalContainer]);

  const renderNextButton = () =>
    nextButtonShowColorAnimation || nextButtonShowConfettiAnimation ? (
      <AnimatedButtonWrapper>
        <AnimatedButton
          confettiColors={nextButtonConfettiColors}
          showColorAnimation={nextButtonShowColorAnimation}
          showConfettiAnimation={nextButtonShowConfettiAnimation}
          successColorway={nextButtonSuccessColorway}
          successContent={nextButtonSuccessText}
          {...sharedButtonProps}
        >
          {sharedButtonText}
        </AnimatedButton>
      </AnimatedButtonWrapper>
    ) : (
      <StyledSubmitButton {...sharedButtonProps}>{sharedButtonText}</StyledSubmitButton>
    );

  return (
    <Container data-testid="OnboardStep" className={className}>
      {hed && <Hed>{hed}</Hed>}
      {dek && <Dek>{dek}</Dek>}
      <StepContent>{children}</StepContent>
      {allFieldsRequired && <AllFieldsRequired>All fields required</AllFieldsRequired>}
      <MobileFixedBtnContainer>{renderNextButton()}</MobileFixedBtnContainer>
      {portalContainerRef?.current && (
        <Portal portalContainer={portalContainerRef?.current}>{portalContent}</Portal>
      )}
      {showLoginCta && currentUserContext.is_guest && (
        <LoginCta>
          Already have an account?{' '}
          <LinkV2 role="button" onClick={toggleIsLogin} inline>
            Log in
          </LinkV2>
          {showTermsPrivacy ? <StyledTermsPrivacy /> : null}
        </LoginCta>
      )}
    </Container>
  );
};

export default OnboardStep;
